import {
  iamShowOptions,
  itemOnlyTypes,
} from "@p0security/shared/assessment/constants";
import { assessmentParse } from "@p0security/shared/assessment/dsl";
import {
  AggregatedGraph,
  AggregatedNode,
} from "@p0security/shared/graph/aggregate";
import {
  DslParseResult,
  ParseWarning,
  isParseWarning,
} from "@p0security/shared/graph/dsl/index";
import { dslParse as parse } from "@p0security/shared/graph/dsl/index";
import { NodeOf } from "@p0security/shared/graph/types";
import { ALL_SCOPE_SENTINEL } from "@p0security/shared/types/assessment";
import {
  AnyAggregates,
  AnyNode,
  AssessmentNodes,
  DefaultTargetNodeType,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { isa } from "@p0security/util/types/is";
import { DiscoverVisualizationProvider } from "components/Assessment/contexts/DiscoverVisualizationContext";
import { CatalogContext } from "components/Catalog/context";
import { ResizableTableColumnsType } from "components/ResizableTable";
import { compact, pickBy, size } from "lodash";
import pluralize from "pluralize";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { UrlUpdateType } from "use-query-params";

import { GraphTable, GraphTableProps } from "../../GraphTable/GraphTable";
import { FindingsContext } from "../contexts/FindingsContext";
import { ScopeContext } from "../contexts/ScopeContext";
import { Controls } from "../hooks/useControls";
import { AssessmentExport } from "./AssessmentExport";
import { DetailedFindingsExport } from "./DetailedFindingsExport";
import { GraphProcessingStep } from "./GraphStep";
import { nodeDataFromShow } from "./node";
import { NodeProperties } from "./node/NodeProperties.tsx";
import { AssessmentCollapse, AssessmentNodeRender } from "./node/renderer";

/** Augments node-search parse warnings with warnings indicating that keytypes
 *  are invalid for the "all" provider scope.
 */
const addInvalidTypeWarnings = (parsed: DslParseResult<AssessmentNodes>) => {
  if (isParseWarning(parsed)) return;
  const invalid = new Set<string>();
  for (const term of parsed) {
    for (const pt of term.pathTerms) {
      if (isa(itemOnlyTypes, pt.keytype)) {
        invalid.add(pt.keytype);
      }
    }
  }
  if (invalid.size) {
    const quoted = [...invalid.keys()].map((i) => `'${i}:'`).join(", ");
    (parsed as any as ParseWarning).warning = `Query ${pluralize(
      "type",
      invalid.size
    )} ${quoted} ${pluralize(
      "is",
      invalid.size
    )} not valid with the 'All targets' scope. Please select a single target.`;
  }
};

export const AssessmentGraph: React.FC<
  Pick<
    GraphTableProps<AssessmentNodes, TargetNodeType, AnyAggregates>,
    | "extra"
    | "frozen"
    | "onSearch"
    | "onSelection"
    | "scopeNode"
    | "selectedNodes"
    | "settingsDisables"
  > & {
    aggregated: AggregatedGraph<AssessmentNodes, AnyAggregates> | undefined;
    controls: Controls;
    onControls: (
      controls: Partial<Controls>,
      updateType?: UrlUpdateType
    ) => void;
    // True if this display is scoped to a single monitor
    isMonitorDisplay?: boolean;
    extraColumns?: ResizableTableColumnsType<AnyNode>;
  }
> = ({
  aggregated,
  controls,
  extraColumns,
  onControls,
  isMonitorDisplay,
  ...graphProps
}) => {
  const catalog = useContext(CatalogContext);
  const { provider, scopeKey, step, percentage } = useContext(ScopeContext);
  const { selectedMonitor } = useContext(FindingsContext);

  const search = useMemo(() => {
    const where = compact([graphProps.frozen?.terms, controls.where]).join(" ");
    const parsed = assessmentParse(where)(parse);
    if (provider === "all") {
      addInvalidTypeWarnings(parsed);
    }
    return parsed;
  }, [controls.where, graphProps.frozen?.terms, provider]);

  const onChangeTerms = useCallback(
    (terms: string) => onControls({ where: terms }, "pushIn"),
    [onControls]
  );

  const graphControls = useMemo(() => {
    const onChangeDisplay = (display: string) =>
      onControls({ display }, "pushIn");
    const onChangeShow = (show: string) =>
      onControls({ show: show as TargetNodeType }, "pushIn");
    return {
      ...controls,
      terms: controls.where,
      onChangeDisplay,
      onChangeTerms,
      onChangeShow,
    };
  }, [controls, onChangeTerms, onControls]);

  const onAddTerm = useCallback(
    (term: string) => {
      return onChangeTerms(
        compact([...controls.where.split(" "), term]).join(" ")
      );
    },
    [controls.where, onChangeTerms]
  );

  const nodeData = useMemo(
    () => nodeDataFromShow[controls.show],
    [controls.show]
  );

  const columns = useMemo((): ResizableTableColumnsType<AnyNode> => {
    const columnProps = {
      catalog,
      onAddTerm,
      provider,
      show: controls.show,
      terms: controls.where,
    };
    return [
      ...(extraColumns ?? []),
      ...nodeData.columns(columnProps, scopeKey),
    ] as ResizableTableColumnsType<AnyNode>;
  }, [
    catalog,
    controls.show,
    controls.where,
    extraColumns,
    nodeData,
    onAddTerm,
    provider,
    scopeKey,
  ]);

  const hasMultipleScopes =
    size(pickBy(selectedMonitor?.countsByScope, (n) => n > 0)) > 1;

  const exporter = useCallback(
    (
      selected: AggregatedNode<AssessmentNodes, TargetNodeType, AnyAggregates>[]
    ) =>
      !isMonitorDisplay ||
      scopeKey === ALL_SCOPE_SENTINEL ||
      !hasMultipleScopes ? (
        <AssessmentExport selected={selected} columns={columns} />
      ) : (
        <DetailedFindingsExport
          selected={selected}
          selectedScope={scopeKey}
          columns={columns}
        />
      ),
    [columns, hasMultipleScopes, isMonitorDisplay, scopeKey]
  );

  const visualize = useMemo(
    () => ({
      collapse: AssessmentCollapse,
      properties: (node: NodeOf<AssessmentNodes>) => (
        <NodeProperties node={node} provider={provider} />
      ),
      render: AssessmentNodeRender,
      fitView: true,
      fitViewOptions: {
        minZoom: 0.5,
        maxZoom: 0.8,
        padding: 0.1,
      },
      highlight: !graphProps.frozen,
    }),
    [graphProps.frozen, provider]
  );

  const showOptions = useMemo(
    () => iamShowOptions(scopeKey === ALL_SCOPE_SENTINEL ? "all" : "item"),
    [scopeKey]
  );

  // Changing to All Targets will hide Resources,
  // so we need to change the show to something else
  // if Resources is selected when this happens
  useEffect(() => {
    if (!showOptions.find((o) => o.value === controls.show)) {
      onControls({ ...controls, show: DefaultTargetNodeType });
    }
  }, [controls, onControls, showOptions]);

  return step === "aggregating" && !!aggregated ? (
    <DiscoverVisualizationProvider provider={provider}>
      <GraphTable<AssessmentNodes, TargetNodeType, AnyAggregates>
        {...graphProps}
        aggregated={aggregated}
        columns={columns}
        controls={graphControls}
        exporter={exporter}
        from={nodeData.predicate}
        showOptions={showOptions}
        search={search}
        visualize={visualize}
      />
    </DiscoverVisualizationProvider>
  ) : (
    <GraphProcessingStep step={step} percentage={percentage} />
  );
};
